import { defineStore } from "pinia";
import type {
  WebModelGetCategory,
  WebModelGetCategoryGroup,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  addNewCategory,
  addNewCategoryGroup,
  deleteCategory,
  deleteCategoryGroup,
  findAllCategories,
  findAllCategoryGroups,
  updateCategory,
  updateCategoryGroup,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withLoading } from "~/helpers/withLoading";
import type { TypeOption } from "~/stores/ui/specific/appsfilterstore";
import { sortBy } from "~/helpers/sortBy";

interface State {
  categories: WebModelGetCategory[];
  categorygroups: WebModelGetCategoryGroup[];
}

export const useCategoriesStore = defineStore("categories", {
  // convert to a function
  state: (): State => ({
    categories: [],
    categorygroups: [],
  }),
  getters: {
    getCategoriesByCategoryGroup: (state) => {
      return (
        categoryGroup: WebModelGetCategoryGroup,
      ): WebModelGetCategory[] => {
        return state.categories
          .filter((category) => category.categoryGroup?.id === categoryGroup.id)
          .toSorted(sortBy({ key: "name", order: "asc" }));
      };
    },
    getCategoryTypeOptionsByCategoryGroup: (state) => {
      return (categoryGroup: WebModelGetCategoryGroup): TypeOption[] => {
        const categoriesToReturn = state.categories
          .filter((category) => category.categoryGroup?.id === categoryGroup.id)
          .toSorted(sortBy({ key: "name", order: "asc" }));

        const typeOptions: TypeOption[] = [];
        for (const element of categoriesToReturn) {
          if (element.id && element.name) {
            typeOptions.push({ value: "" + element.id, text: element.name });
          }
        }
        return typeOptions;
      };
    },
  },
  actions: {
    async loadCategoryGroups() {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(findAllCategoryGroups())),
      );
      console.debug("loadCategoryGroups: ", data, error);
      if (data) {
        this.categorygroups = data;
      }
    },
    async loadCategories() {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(findAllCategories())),
      );
      console.debug("loadCategories: ", data, error);
      if (data) {
        this.categories = data;
      }
    },

    // admin only - category groups
    async createCategoryGroup(name: string) {
      const { data } = await withErrorMessage(
        withLoading(addNewCategoryGroup({ name })),
      );
      console.debug("createCategoryGroup: ", data);

      if (data) {
        await this.loadCategoryGroups();
      }
      return data;
    },
    async updateCategoryGroup(id: number, name: string) {
      const { data } = await withErrorMessage(
        withLoading(updateCategoryGroup(id, { name })),
      );
      console.debug("updateCategoryGroup: ", data);

      if (data) {
        await this.loadCategoryGroups();
      }
      return data;
    },
    async deleteCategoryGroup(id: number) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(deleteCategoryGroup(id))),
      );
      console.debug("deleteCategoryGroup: ", data, error);
      if (!error) {
        await Promise.all([this.loadCategoryGroups(), this.loadCategories()]);
      }
      return !error;
    },

    // admin only - categories
    async createCategory(name: string, categoryGroupId: number) {
      const { data } = await withErrorMessage(
        withLoading(addNewCategory({ name, categoryGroupId })),
      );
      if (data) {
        await this.loadCategories();
      }
      return data;
    },

    async updateCategory(id: number, name: string, categoryGroupId: number) {
      const { data } = await withErrorMessage(
        withLoading(updateCategory(id, { name, categoryGroupId })),
      );
      if (data) {
        await this.loadCategories();
      }
      return data;
    },

    async deleteCategory(id: number) {
      await withErrorHandling(
        withErrorMessage(withLoading(deleteCategory(id))),
      );
      await this.loadCategories();
    },

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
